<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import AnalyticsPanel from '../analytics/AnalyticsPanel.vue';
import { useAnalyticsFormat } from '../analytics/useAnalyticsFormat';
import { campanhasLabel } from './campanhasLabels';
import {
  META_ERROR_GROUPS,
  metaErrorGroup,
  metaErrorGroupLabel,
  metaErrorText,
} from './metaErrors';

// As falhas por motivo: o código da Meta em português, pelo `metaErrors.js`
// (a única cópia do texto), agrupado pelo que fazer com ele; a falha sem
// código, pela classe do texto; e quem foi pulado sem chegar a ter mensagem.
const props = defineProps({
  // A resposta de GET campanhas/falhas; null enquanto não chegou.
  report: { type: Object, default: null },
  failed: { type: Boolean, default: false },
});

const { t } = useI18n();
const { number } = useAnalyticsFormat();

const groups = computed(() => {
  const byGroup = new Map();
  (props.report?.failed || []).forEach(item => {
    const group = metaErrorGroup(item.code);
    if (!byGroup.has(group)) byGroup.set(group, []);
    byGroup.get(group).push({
      key: `meta-${item.code}`,
      code: item.code,
      text: metaErrorText(t, item.code),
      metaTitle: item.meta_title,
      count: item.count,
    });
  });
  return META_ERROR_GROUPS.filter(group => byGroup.has(group)).map(group => ({
    key: group,
    label: metaErrorGroupLabel(t, group),
    items: byGroup.get(group),
  }));
});

const unparsed = computed(() =>
  (props.report?.unparsed || []).map(item => ({
    key: `unparsed-${item.class}`,
    text: campanhasLabel(t, 'unparsed', item.class),
    count: item.count,
  }))
);

const skipped = computed(() =>
  (props.report?.skipped || []).map(item => ({
    key: `skipped-${item.reason}`,
    text: campanhasLabel(t, 'skipped', item.reason),
    count: item.count,
  }))
);

const nothing = computed(
  () =>
    props.report &&
    !props.report.failed_total &&
    !props.report.skipped_total &&
    !unparsed.value.length
);
</script>

<template>
  <AnalyticsPanel
    data-test-id="campanha-falhas"
    :title="t('CAMPANHAS.FAILURES.TITLE')"
    :description="
      report
        ? t('CAMPANHAS.FAILURES.DESCRIPTION', {
            failed: number(report.failed_total || 0),
            skipped: number(report.skipped_total || 0),
          })
        : ''
    "
  >
    <p v-if="failed" class="mb-0 text-sm text-n-ruby-11">
      {{ t('ATENDIMENTO_REPORTS.FAILED') }}
    </p>
    <p v-else-if="nothing" class="mb-0 text-sm text-n-slate-11">
      {{ t('CAMPANHAS.FAILURES.NONE') }}
    </p>
    <div v-else-if="report" class="flex flex-col gap-4 min-w-0">
      <section
        v-for="group in groups"
        :key="group.key"
        :data-test-id="`campanha-falhas-grupo-${group.key}`"
        class="flex flex-col gap-1 min-w-0"
      >
        <h3 class="text-sm font-medium text-n-slate-12">{{ group.label }}</h3>
        <ul class="flex flex-col m-0 list-none divide-y divide-n-weak">
          <li
            v-for="item in group.items"
            :key="item.key"
            :data-test-id="`campanha-falha-${item.code}`"
            class="flex gap-3 justify-between items-baseline py-1.5 min-w-0 text-sm"
          >
            <span class="flex flex-col min-w-0">
              <span class="break-words text-n-slate-12">{{ item.text }}</span>
              <span class="text-xs text-n-slate-10">
                {{
                  item.metaTitle
                    ? t('CAMPANHAS.FAILURES.CODE_WITH_TITLE', {
                        code: item.code,
                        title: item.metaTitle,
                      })
                    : t('CAMPANHAS.FAILURES.CODE', { code: item.code })
                }}
              </span>
            </span>
            <span class="font-medium tabular-nums text-n-slate-12">
              {{ number(item.count) }}
            </span>
          </li>
        </ul>
      </section>
      <section
        v-if="unparsed.length"
        data-test-id="campanha-falhas-sem-codigo"
        class="flex flex-col gap-1 min-w-0"
      >
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ t('CAMPANHAS.FAILURES.UNPARSED') }}
        </h3>
        <ul class="flex flex-col m-0 list-none divide-y divide-n-weak">
          <li
            v-for="item in unparsed"
            :key="item.key"
            class="flex gap-3 justify-between items-baseline py-1.5 text-sm"
          >
            <span class="text-n-slate-12">{{ item.text }}</span>
            <span class="font-medium tabular-nums text-n-slate-12">
              {{ number(item.count) }}
            </span>
          </li>
        </ul>
      </section>
      <section
        v-if="skipped.length"
        data-test-id="campanha-falhas-puladas"
        class="flex flex-col gap-1 min-w-0"
      >
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ t('CAMPANHAS.FAILURES.SKIPPED') }}
        </h3>
        <ul class="flex flex-col m-0 list-none divide-y divide-n-weak">
          <li
            v-for="item in skipped"
            :key="item.key"
            class="flex gap-3 justify-between items-baseline py-1.5 text-sm"
          >
            <span class="text-n-slate-12">{{ item.text }}</span>
            <span class="font-medium tabular-nums text-n-slate-12">
              {{ number(item.count) }}
            </span>
          </li>
        </ul>
      </section>
    </div>
  </AnalyticsPanel>
</template>
